/* eslint-disable react/prop-types */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CircleFadingPlus,
  ExternalLink,
  LifeBuoy,
  UserRound,
} from "lucide-react";

import {
  fetchMemberCollection,
  handleCollectionImageError,
} from "@/lib/wallet";

import AuthStatus from "@/components/AuthStatus";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getAuthedMemberProfile } from "@/lib/authz";

function MembersHome() {
  const [firstName, setFirstName] = React.useState("");
  const [collection, setCollection] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setLoadError("");

      const member = await getAuthedMemberProfile();
      if (!alive) return;

      setFirstName(member.profile?.first_name ?? "");

      const uid = member.session?.user?.id;
      const items = uid ? await fetchMemberCollection(uid) : [];
      if (!alive) return;

      setCollection(items);
    })()
      .catch((error) => {
        console.error(error);
        if (alive) {
          setLoadError("Your collection could not be loaded right now.");
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const featured = collection[0] ?? null;

  const collectionPreview = collection.slice(0, 3);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-8 md:pt-12">
      <header className="mb-8 px-1">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-black/45">
          Palais Antoia
        </p>
        <h1 className="mt-2 font-kalice text-4xl font-medium leading-tight md:text-5xl">
          Welcome{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="mt-3 max-w-2xl font-sans text-base leading-relaxed text-black/60">
          Everything you create with Antoia is saved here in your personal collection.
        </p>
      </header>

      {loading ? (
        <Card className="min-h-[28rem] animate-pulse rounded-[2rem] border-white/30 bg-[#111]" />
      ) : loadError ? (
        <Card className="rounded-[2rem] border border-red-900/10 bg-white/55">
          <CardContent className="p-8 font-sans text-sm text-red-700">
            {loadError}
          </CardContent>
        </Card>
      ) : featured ? (
        <FeaturedCreation creation={featured} />
      ) : (
        <EmptyCollectionHero />
      )}

      <section className="mt-12">
        <div className="flex items-end justify-between gap-4 px-1">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.16em] text-black/45">
              Personal archive
            </p>
            <h2 className="mt-1 font-kalice text-3xl font-medium md:text-4xl">
              Your collection
            </h2>
          </div>

          {collection.length > 0 && (
            <Button
              asChild
              variant="ghost"
              className="rounded-full px-3 font-sans hover:bg-white/60 hover:text-black"
            >
              <Link to="/wallet">
                View all
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>

        {collectionPreview.length > 0 ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {collectionPreview.map((item) => (
              <CollectionPreviewCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-3xl border border-white bg-white/45 p-6 font-sans text-sm leading-relaxed text-black/55">
            {collection.length > 0
              ? "Your future Antoia creations will appear here."
              : "Your future Antoia creations will appear here."}
          </div>
        )}
      </section>

      <section className="mt-12 grid gap-4 md:grid-cols-[1.35fr_0.65fr]">
        <Card className="rounded-[2rem] border-2 border-white bg-white/55">
          <CardContent className="p-7 md:p-9">
            <p className="font-sans text-xs uppercase tracking-[0.16em] text-black/45">
              About Antoia
            </p>
            <h2 className="mt-3 max-w-xl font-kalice text-3xl font-medium leading-tight">
              Personal meaning, translated into form.
            </h2>
            <p className="mt-4 max-w-2xl font-sans text-sm leading-relaxed text-black/60 md:text-base">
              Palais Antoia transforms personal choices, memories and data into
              experiences, artworks and made-to-order items. Your collection
              keeps these different encounters connected over time.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                asChild
                variant="outline"
                className="rounded-full border-black/15 bg-transparent font-sans hover:bg-black hover:text-white"
              >
                <Link to="/">
                  Discover Antoia
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                className="rounded-full px-4 font-sans hover:bg-white hover:text-black"
              >
                <Link to="/palaisantoia/trionfi">
                  Trionfi archive
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[2rem] border-2 border-white bg-white/55">
          <CardContent className="flex h-full flex-col p-7 md:p-9">
            <p className="font-sans text-xs uppercase tracking-[0.16em] text-black/45">
              Account
            </p>
            <div className="mt-4 grid gap-2">
              <Button
                asChild
                variant="outline"
                className="justify-start rounded-xl border-black/10 bg-transparent font-sans hover:bg-black hover:text-white"
              >
                <Link to="/settings">
                  <UserRound className="mr-2 h-4 w-4" />
                  Profile settings
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="justify-start rounded-xl border-black/10 bg-transparent font-sans hover:bg-black hover:text-white"
              >
                <Link to="/contact">
                  <LifeBuoy className="mr-2 h-4 w-4" />
                  Support
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      <div className="mt-8">
        <AuthStatus className="justify-center" />
      </div>
    </div>
  );
}

function FeaturedCreation({ creation }) {
  const isTrionfi = Boolean(creation.trionfi);

  return (
    <Card className="overflow-hidden rounded-[2rem] border-0 border-white/30 bg-[#1b1515] text-white shadow-sm">
      <CardContent className="grid gap-0 p-0 md:grid-cols-[0.72fr_1.28fr]">
      <div className="flex min-h-[24rem] items-center justify-center bg-[#1b1515] p-7 md:min-h-[32rem] md:p-10">
          <img
            src={creation.image}
            alt={creation.title}
            onError={(event) =>
              handleCollectionImageError(event, creation.fallbackImage)
            }
            className={[
              "h-auto max-h-[30rem] w-full shadow-[0_28px_75px_rgba(0,0,0,0.4)]",
              isTrionfi
                ? "aspect-[7/12.1] max-w-[17rem] rounded-[1.4rem]"
                : "aspect-[4/5] max-w-[22rem] rounded-2xl",
              creation.imageFit === "contain" ? "object-contain" : "object-cover",
            ].join(" ")}
            draggable={false}
          />
        </div>

        <div className="flex flex-col justify-center p-7 md:p-12">
          {/* <p className="font-sans text-xs uppercase tracking-[0.18em] text-white/45">
            {isTrionfi ? "Latest creation" : "Latest creation"}
          </p> */}

          {creation.accessionNumber && (
            <p className="mt-8 font-sans text-xs uppercase tracking-[0.16em] text-white/40">
              {creation.accessionNumber}
            </p>
          )}

          {creation.variant && (
            <p className="mt-3 font-sans text-base text-white/55">
              {creation.variant}
            </p>
          )}

          <h2 className="mt-1 font-kalice text-4xl font-medium leading-tight text-white md:text-5xl">
            {creation.title}
          </h2>

          <p className="mt-5 max-w-md font-sans text-sm leading-relaxed text-white/65 md:text-base">
            {creation.origin}
            {creation.date ? ` · ${creation.date}` : ""}
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            <Button
              asChild
              className="rounded-full bg-white px-6 font-sans text-black hover:bg-white/85 hover:text-black"
            >
              <Link to={creation.route}>
                {isTrionfi ? "Open your card" : "View creation"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>

            {isTrionfi && (
              <Button
                asChild
                variant="outline"
                className="rounded-full border-white/25 bg-transparent px-6 font-sans text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/palaisantoia/trionfi">
                  View Trionfi archive
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CollectionPreviewCard({ item }) {
  return (
    <Link
      to={item.route}
      className="group hover:text-black overflow-hidden rounded-3xl border-2 border-white bg-white/55 p-3 transition hover:-translate-y-0.5 hover:bg-white/75"
    >
      <div className="overflow-hidden rounded-2xl bg-black/5">
      <img
          src={item.image}
          alt={item.title}
          onError={(event) =>
            handleCollectionImageError(event, item.fallbackImage)
          }
          className={[
            "w-full transition duration-500 group-hover:scale-[1.02]",
            item.trionfi ? "aspect-[4/5]" : "aspect-[4/5]",
            item.imageFit === "object-cover" ? "object-contain" : "object-cover",
          ].join(" ")}
          draggable={false}
        />
      </div>
      <div className="flex items-end justify-between gap-4 p-3 pb-2 pt-4">
        <div className="min-w-0">
          <p className="font-sans text-xs uppercase tracking-[0.14em] text-black/40">
            {item.eyebrow}
          </p>
          <h3 className="mt-1 truncate font-kalice text-2xl font-medium">
            {item.title}
          </h3>
          {item.date && (
            <p className="mt-1 font-sans text-xs text-black/45">{item.date}</p>
          )}
        </div>
        <ArrowRight className="mb-1 h-4 w-4 shrink-0 transition group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

function EmptyCollectionHero() {
  return (
    <Card className="overflow-hidden rounded-[2rem] border-2 border-white/30 bg-[#111] text-white">
      <CardContent className="flex min-h-[25rem] flex-col items-start justify-end p-8 md:p-12">
        <CircleFadingPlus className="h-9 w-9 text-[#ff813d]" strokeWidth={1.5} />
        <p className="mt-8 font-sans text-xs uppercase tracking-[0.18em] text-white/45">
          Your collection
        </p>
        <h2 className="mt-2 max-w-xl font-kalice text-4xl font-medium leading-tight md:text-5xl">
          Your future Antoia creations will appear here.
        </h2>
        <p className="mt-4 max-w-lg font-sans text-sm leading-relaxed text-white/60 md:text-base">
          Experiences and personal pieces remain connected to your account, so
          you can return to them whenever you choose.
        </p>
      </CardContent>
    </Card>
  );
}

export default MembersHome;